// Deep-link helpers for WAF Events list (`/v2/detection/waf_event`).

import { getWafEventStatus } from "./wafEventListUtils";

function pushFilter(filters, id, label, value) {
  const text = value != null ? String(value).trim() : "";
  if (!text) return;
  filters.push({ id, label, value: text });
}

/**
 * Local wall-clock for custom range API — keep milliseconds so list
 * `fromUnixTimestamp64Milli` bounds match DateTime64 burst first/last_seen.
 * Optional deltaMs covers CH↔ISO↔local rounding (View related uses ±1s).
 */
function toPickerDateTimeValueMs(iso, deltaMs = 0) {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  if (deltaMs) d.setTime(d.getTime() + deltaMs);
  const pad = (n) => String(n).padStart(2, "0");
  const ms = String(d.getMilliseconds()).padStart(3, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}.${ms}`;
}

const SIMILAR_FILTER_DEFS = {
  ip: {
    id: "src_ip",
    label: "IP Address",
    getValue: (event) => event?.attacker?.source_ip || event?.attacker?.ip,
  },
  hostname: {
    id: "hostname",
    label: "Hostname",
    getValue: (event) => event?.target?.host,
  },
  uri: {
    id: "uri",
    label: "URI Path",
    getValue: (event) => event?.target?.uri || event?.target?.path,
  },
  attack_type: {
    id: "attack_type",
    label: "Attack Type",
    getValue: (event) => event?.primary_intent?.intent_name
      || (Array.isArray(event?.attack_types) ? event.attack_types[0] : null),
  },
  country: {
    id: "country",
    label: "Country",
    getValue: (event) => event?.attacker?.geo?.country_iso,
  },
  status: {
    id: "status",
    label: "Status",
    getValue: (event) => getWafEventStatus({ action: event?.assessment?.action }),
  },
};

/** Build location.state for a single similar-event filter from a v3 envelope. */
export function buildSimilarWafEventNavigationState(event, filterType) {
  if (!event) return null;
  if (filterType === "burst" || filterType === "related") {
    return buildRelatedWafEventNavigationState(event);
  }

  const def = SIMILAR_FILTER_DEFS[filterType];
  if (!def) return null;

  const initialFilters = [];
  pushFilter(initialFilters, def.id, def.label, def.getValue(event));
  if (initialFilters.length === 0) return null;

  // Similar-filter clicks must not inherit View related's burst custom window.
  // Reset to the page default period so only the filter pill applies.
  return { initialFilters, timeRange: "7d" };
}

/** Build location.state for related burst events from a v3 envelope row. */
export function buildRelatedWafEventNavigationState(event) {
  if (!event?.correlation) return null;

  const { correlation, attacker, target, primary_intent } = event;
  const initialFilters = [];

  pushFilter(initialFilters, "src_ip", "IP Address", attacker?.source_ip || attacker?.ip);
  pushFilter(initialFilters, "hostname", "Hostname", target?.host);
  pushFilter(initialFilters, "uri", "URI Path", target?.uri || target?.path);
  pushFilter(
    initialFilters,
    "attack_type",
    "Attack Type",
    primary_intent?.intent_name
      || (Array.isArray(event.attack_types) ? event.attack_types[0] : null),
  );

  if (initialFilters.length === 0) return null;

  // Burst count is Smart Parse only; exclude Signature WAF from the list.
  pushFilter(initialFilters, "waf_type", "Source", "smart_parse");

  // ±1s slack: CH first/last_seen can be second-truncated or lose ms in transit.
  const from = toPickerDateTimeValueMs(correlation.first_seen, -1000);
  const to = toPickerDateTimeValueMs(correlation.last_seen, 1000);
  if (!from || !to) return { initialFilters };

  return {
    initialFilters,
    timeRange: "custom",
    customDateRange: [from, to],
    customRangeConfirm: true,
  };
}

export const WAF_EVENTS_PATH = "/v2/detection/waf_event";
